import React, { useState, useMemo } from 'react';
import { getTemplates } from '../data/templates';
import { saveCustomProgram } from '../data/programs';
import { formatWeight } from '../utils/calculations';

const LIFT_ORDER = ['squat', 'bench', 'deadlift'];

const liftColors = {
  bench: 'var(--bench-color)', squat: 'var(--squat-color)', deadlift: 'var(--deadlift-color)',
};

// Labels/colors for tunable starting-weight fields
const FIELD_META = {
  squat: { label: 'Squat', color: liftColors.squat },
  bench: { label: 'Bench Press', color: liftColors.bench },
  press: { label: 'Overhead Press', color: liftColors.bench },
  deadlift: { label: 'Deadlift', color: liftColors.deadlift },
  row: { label: 'Barbell Row', color: 'var(--accent)' },
};

const INCREMENT_OPTIONS = [1.25, 2.5, 5];

const LEVEL_ORDER = ['Novice', 'Intermediate', 'Advanced'];

export default function ProgramImporter({ settings, onImport, onCancel, embedded }) {
  const templates = getTemplates();
  const [screen, setScreen] = useState('list');
  const [templateId, setTemplateId] = useState(null);
  const [config, setConfig] = useState(null);

  const template = templateId ? templates.find(t => t.id === templateId) : null;

  const generated = useMemo(() => {
    if (!template || !config) return null;
    return template.generate(config);
  }, [template, config]);

  const handlePick = (t) => {
    setTemplateId(t.id);
    setConfig(t.getDefaults(settings));
    setScreen('configure');
  };

  const setStart = (key, value) => {
    setConfig(c => ({ ...c, starts: { ...c.starts, [key]: value } }));
  };

  const setIncrement = (key, value) => {
    setConfig(c => ({ ...c, increments: { ...c.increments, [key]: value } }));
  };

  const handleConfirm = () => {
    if (!generated) return;
    for (const lift of LIFT_ORDER) saveCustomProgram(generated[lift]);
    onImport(generated);
  };

  const missing1RM = LIFT_ORDER.filter(l => !(settings[`${l}1RM`] > 0));

  // =====================================================
  // TEMPLATE LIST
  // =====================================================
  if (screen === 'list') {
    return (
      <div>
        <div className="session-header">
          <h1>{embedded ? 'Pick a Program' : 'Import a Program'}</h1>
          <p className="sub">Built for you from your current maxes</p>
        </div>

        {missing1RM.length > 0 && (
          <div className="card" style={{ padding: 12, marginBottom: 8, borderLeft: '2px solid var(--danger)' }}>
            <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
              Set your {missing1RM.join(', ')} 1RM first — starting weights are calculated from it.
            </div>
          </div>
        )}

        {LEVEL_ORDER.map(level => {
          const group = templates.filter(t => t.level === level);
          if (group.length === 0) return null;
          return (
            <div key={level}>
              <div className="section-divider">{level}</div>
              {group.map(t => (
                <div key={t.id} className="card" style={{ padding: 14, marginBottom: 8, cursor: 'pointer' }}
                  onClick={() => handlePick(t)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{
                      fontFamily: 'Barlow Condensed, sans-serif', fontSize: 17, fontWeight: 600,
                    }}>{t.name}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', marginLeft: 8 }}>
                      {t.level}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    {t.author} · {t.frequency}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 8, lineHeight: 1.45 }}>
                    {t.summary}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onCancel}>
          {embedded ? 'Back' : 'Cancel'}
        </button>
      </div>
    );
  }

  // =====================================================
  // CONFIGURE
  // =====================================================
  if (screen === 'configure' && template && config) {
    const opts = template.options || {};
    const startKeys = opts.startLabels || [];
    const incKeys = opts.incrementKeys || [];
    const toggles = opts.toggles || [];

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <button className="btn btn-secondary btn-small" style={{ width: 'auto' }}
            onClick={() => setScreen('list')}>←</button>
          <div>
            <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 18, fontWeight: 600 }}>
              {template.name}
            </h1>
            <p className="sub">{template.level} · {template.frequency}</p>
          </div>
        </div>

        <div className="card" style={{ padding: 14, marginBottom: 8 }}>
          <div style={labelStyle}>How it runs</div>
          {template.structure.map((line, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text-dim)', padding: '2px 0', lineHeight: 1.4 }}>
              · {line}
            </div>
          ))}
        </div>

        {startKeys.length > 0 && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>Starting weights</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.45 }}>
              Seeded from your 1RMs — adjust if you know better numbers.
            </div>
            {startKeys.map(key => (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 0', borderBottom: '1px solid var(--border)',
              }}>
                <span style={{ fontSize: 13, color: FIELD_META[key]?.color }}>{FIELD_META[key]?.label || key}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" inputMode="decimal" value={config.starts?.[key] ?? ''}
                    onChange={e => setStart(key, parseFloat(e.target.value) || 0)}
                    style={{ width: 80, textAlign: 'right', padding: 6, fontSize: 15, fontWeight: 600 }} />
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>kg</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {incKeys.length > 0 && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>Weight added per {template.id === 'texas_method' ? 'week' : 'session'}</div>
            {incKeys.map(key => (
              <div key={key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 0', gap: 8,
              }}>
                <span style={{ fontSize: 13, color: FIELD_META[key]?.color, flex: 1 }}>{FIELD_META[key]?.label || key}</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  {INCREMENT_OPTIONS.map(inc => (
                    <button key={inc} onClick={() => setIncrement(key, inc)}
                      style={{ ...pillStyle(config.increments?.[key] === inc, FIELD_META[key]?.color), minWidth: 52 }}>
                      +{inc}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {toggles.map(t => (
          <div key={t.key} className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>{t.label}</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: t.help ? 8 : 0 }}>
              {t.choices.map(ch => (
                <button key={String(ch.value)}
                  onClick={() => setConfig(c => ({ ...c, [t.key]: ch.value }))}
                  style={pillStyle(config[t.key] === ch.value)}>
                  {ch.label}
                </button>
              ))}
            </div>
            {t.help && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.45 }}>{t.help}</div>
            )}
          </div>
        ))}

        {opts.weeksRange && (
          <div className="card" style={{ padding: 14, marginBottom: 8 }}>
            <div style={labelStyle}>Cycle length</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button style={stepperBtnStyle}
                onClick={() => setConfig(c => ({ ...c, weeks: Math.max(opts.weeksRange[0], c.weeks - 1) }))}>−</button>
              <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 22, fontWeight: 600, minWidth: 30, textAlign: 'center' }}>
                {config.weeks}
              </span>
              <button style={stepperBtnStyle}
                onClick={() => setConfig(c => ({ ...c, weeks: Math.min(opts.weeksRange[1], c.weeks + 1) }))}>+</button>
              <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>weeks</span>
            </div>
          </div>
        )}

        <button className="btn btn-primary" style={{ marginTop: 4 }} onClick={() => setScreen('preview')}>
          Preview Program
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onCancel}>
          {embedded ? 'Back' : 'Cancel'}
        </button>
      </div>
    );
  }

  // =====================================================
  // PREVIEW
  // =====================================================
  if (screen === 'preview' && template && generated) {
    const totalWeeks = generated.squat.weeks.length;

    const weekBlock = (weekIdx, title) => (
      <div className="card" style={{ padding: 14, marginBottom: 8 }}>
        <div style={labelStyle}>{title}</div>
        {LIFT_ORDER.map(lift => {
          const prog = generated[lift];
          const week = prog.weeks[weekIdx];
          if (!week) return null;
          return (
            <div key={lift} style={{ marginBottom: 10 }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: liftColors[lift],
                fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase', marginBottom: 2,
              }}>
                {prog.displayName.replace(`${template.name} — `, '')}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>
                  {week.days.length} day{week.days.length !== 1 ? 's' : ''}
                </span>
              </div>
              {week.days.map((day, di) => (
                <div key={di} style={{ marginBottom: 4 }}>
                  {week.days.length > 1 && (
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                      Day {di + 1}
                    </div>
                  )}
                  {day.exercises.map((e, ei) => (
                    <div key={ei} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '2px 0',
                      fontSize: 12, color: e.isAccessory ? 'var(--text-muted)' : 'var(--text-dim)',
                    }}>
                      <span>{e.name}</span>
                      <span style={{ color: '#888', whiteSpace: 'nowrap', marginLeft: 8 }}>
                        {e.sets}×{e.reps}{typeof e.weight === 'number' ? ` · ${formatWeight(e.weight)}kg` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <button className="btn btn-secondary btn-small" style={{ width: 'auto' }}
            onClick={() => setScreen('configure')}>←</button>
          <div>
            <h1 style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 18, fontWeight: 600 }}>
              {template.name}
            </h1>
            <p className="sub">{totalWeeks} weeks</p>
          </div>
        </div>

        {weekBlock(0, 'Week 1')}
        {weekBlock(totalWeeks - 1, `Week ${totalWeeks} — where you end up`)}

        <div className="card" style={{ padding: 14, marginBottom: 8, borderLeft: '2px solid var(--max-test-border)' }}>
          <div style={labelStyle}>Before you start</div>
          {template.caveats.map((c, i) => (
            <div key={i} style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 0', lineHeight: 1.45 }}>
              · {c}
            </div>
          ))}
          {!embedded && (
            <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '3px 0', lineHeight: 1.45 }}>
              · This replaces your active program on all three lifts and resets progress to Week 1.
              Training history is preserved.
            </div>
          )}
        </div>

        <button className="btn btn-primary" onClick={handleConfirm}>
          {embedded ? 'Start This Program' : 'Import & Activate All Three Lifts'}
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={() => setScreen('configure')}>
          Adjust
        </button>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onCancel}>
          {embedded ? 'Back' : 'Cancel'}
        </button>
      </div>
    );
  }

  return null;
}

const labelStyle = {
  fontSize: 12, color: 'var(--text-dim)', fontFamily: 'Barlow Condensed, sans-serif',
  textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8,
};

function pillStyle(active, activeColor) {
  const c = activeColor || 'var(--accent)';
  return {
    flex: 1, padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${active ? c : 'var(--border)'}`,
    background: active ? `${c}15` : 'none',
    color: active ? c : 'var(--text-dim)',
    fontFamily: 'Barlow Condensed, sans-serif', fontSize: 13, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

const stepperBtnStyle = {
  width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-elevated)', color: 'var(--text-dim)', fontSize: 16,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
